import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Edit3, FileText, X } from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";

const STATUSES = [
  { value: "draft", label: "Draft", color: "bg-zinc-700" },
  { value: "public", label: "Public", color: "bg-green-600" },
  { value: "private", label: "Privat", color: "bg-red-600" },
];

const TEMPLATES = [
  { value: "default", label: "Standard" },
  { value: "full-width", label: "Full width" },
  { value: "sidebar-right", label: "Sidebar dreapta" },
  { value: "sidebar-left", label: "Sidebar stânga" },
];

const emptyPage = () => ({
  title: "",
  slug: "",
  template: "default",
  content: "",
  excerpt: "",
  status: "draft",
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  show_in_nav: false,
  order: 0,
});

export default function IDGPagesTab() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/idg/pages").then((r) => setPages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/idg/pages/${editing.id}`, editing);
        toast.success("Pagină actualizată");
      } else {
        await api.post("/idg/pages", editing);
        toast.success("Pagină creată");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sigur vrei să ștergi această pagină?")) return;
    await api.delete(`/idg/pages/${id}`);
    toast.success("Șters");
    load();
  };

  return (
    <div className="space-y-4" data-testid="idg-pages-tab">
      {!editing ? (
        <>
          <div className="flex items-center justify-between">
            <h3 className="tf-section-title">Pagini ({pages.length})</h3>
            <button onClick={() => setEditing(emptyPage())} className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-1" data-testid="idg-page-new">
              <Plus className="w-3.5 h-3.5" /> Pagină nouă
            </button>
          </div>

          {/* System pages section */}
          {pages.some(p => p.is_system) && (
            <div className="space-y-1">
              <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-600 px-1 mb-2">
                Pagini sistem (built-in)
              </div>
              {pages.filter(p => p.is_system).sort((a, b) => (a.order || 0) - (b.order || 0)).map((p) => (
                <div key={p.id} className="bg-[#090b10] border border-white/5 tf-clip-card px-4 py-3 flex items-center gap-3" data-testid={`idg-page-row-${p.id}`}>
                  <div className="w-1 h-8 rounded shrink-0" style={{ background: 'var(--tf-primary)' }} />
                  <FileText className="w-4 h-4 text-[var(--tf-primary)] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-white text-sm">{p.title}</span>
                      <span className="text-[8px] px-1.5 py-0.5 bg-[var(--tf-primary)]/15 text-[var(--tf-primary)] border border-[var(--tf-primary)]/30 tf-clip-card-sm font-mono font-bold uppercase tracking-widest">SISTEM</span>
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 flex items-center gap-2 mt-0.5">
                      <span>{p.route || `/${p.slug}`}</span>
                      {p.excerpt && <span className="text-zinc-700">·</span>}
                      <span className="text-zinc-600 truncate max-w-xs">{p.excerpt}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider tf-clip-card-sm ${STATUSES.find((s) => s.value === p.status)?.color || "bg-zinc-700"} text-white shrink-0`}>
                    {p.status}
                  </span>
                  <a href={p.route || `/${p.slug}`} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-[var(--tf-primary)] p-1 transition-colors" title="Deschide pagina">
                    <FileText className="w-3.5 h-3.5" />
                  </a>
                  <button onClick={() => setEditing(p)} className="text-zinc-400 hover:text-white p-1" data-testid={`idg-page-edit-${p.id}`}>
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <div className="w-6 h-6 flex items-center justify-center text-zinc-700 cursor-not-allowed" title="Paginile sistem nu pot fi șterse">
                    <Trash2 className="w-3.5 h-3.5" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Custom pages section */}
          {pages.filter(p => !p.is_system).length > 0 && (
            <div className="space-y-1">
              <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-600 px-1 mb-2 mt-4">
                Pagini personalizate
              </div>
              {pages.filter(p => !p.is_system).map((p) => (
                <div key={p.id} className="bg-[#090b10] border border-white/5 tf-clip-card px-4 py-3 flex items-center gap-3" data-testid={`idg-page-row-${p.id}`}>
                  <FileText className="w-4 h-4 text-zinc-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-semibold text-white truncate">{p.title}</div>
                    <div className="text-[10px] font-mono text-zinc-500">/{p.slug} · {p.template}</div>
                  </div>
                  <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider tf-clip-card-sm ${STATUSES.find((s) => s.value === p.status)?.color || "bg-zinc-700"} text-white`}>
                    {p.status}
                  </span>
                  <button onClick={() => setEditing(p)} className="text-zinc-400 hover:text-white p-1" data-testid={`idg-page-edit-${p.id}`}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.id)} className="text-red-500 hover:bg-red-500/10 p-1" data-testid={`idg-page-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}

          {pages.length === 0 && (
            <p className="bg-[#090b10] border border-white/5 tf-clip-card p-8 text-center text-zinc-500 text-sm font-mono">
              Nicio pagină încă. Creează prima ta pagină.
            </p>
          )}
        </>

      ) : (
        <div className="tf-surface tf-clip-card p-5 space-y-3" data-testid="idg-page-form">
          <div className="flex items-center justify-between">
            <h3 className="tf-section-title">{editing.id ? "Editează pagina" : "Pagină nouă"}</h3>
            <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Titlu" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testid="page-title" />
            <Field label="Slug (/p/slug)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="page-slug" />
            <Select label="Template" value={editing.template} onChange={(v) => setEditing({ ...editing, template: v })} options={TEMPLATES} testid="page-template" />
            <Select label="Status" value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} options={STATUSES.map((s) => ({ value: s.value, label: s.label }))} testid="page-status" />
          </div>
          <Field label="Rezumat scurt" value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} textarea testid="page-excerpt" />
          <div>
            <label className="tf-section-title block mb-1.5">Conținut</label>
            <RichTextEditor value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} testid="page-content-editor" minHeight={300} />
          </div>
          <div className="border-t border-white/5 pt-3 space-y-3">
            <h4 className="tf-section-title text-[10px]">SEO</h4>
            <Field label="Meta title" value={editing.meta_title} onChange={(v) => setEditing({ ...editing, meta_title: v })} testid="page-meta-title" />
            <Field label="Meta description" value={editing.meta_description} onChange={(v) => setEditing({ ...editing, meta_description: v })} textarea testid="page-meta-description" />
            <Field label="Meta keywords (comma-separated)" value={editing.meta_keywords} onChange={(v) => setEditing({ ...editing, meta_keywords: v })} testid="page-meta-keywords" />
          </div>
          <div className="flex items-center gap-4 pt-2 border-t border-white/5">
            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input type="checkbox" checked={!!editing.show_in_nav} onChange={(e) => setEditing({ ...editing, show_in_nav: e.target.checked })} data-testid="page-show-in-nav" />
              Arată în meniu navigare
            </label>
            <Field label="Ordine" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v) || 0 })} compact testid="page-order" />
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={save} className="tf-btn-primary tf-clip-btn flex items-center gap-1" data-testid="page-save"><Save className="w-3.5 h-3.5" /> Salvează</button>
            <button onClick={() => setEditing(null)} className="tf-btn-secondary tf-clip-btn">Anulează</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, compact, testid }) {
  return (
    <div className={compact ? "w-32" : ""}>
      <label className="text-xs tf-section-title block mb-1">{label}</label>
      {textarea ? (
        <textarea className="tf-input" rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      ) : (
        <input type={type} className="tf-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      )}
    </div>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs tf-section-title block mb-1">{label}</label>
      <select className="tf-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
